import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  Check,
  TrendingUp,
  ChevronUp,
  Info,
  Loader,
} from "lucide-react";
import { CurrencySelector } from "./CurrencySelector";
import {
  formatSmartDecimal,
  formatNumbers,
  getTokenSymbol,
} from "../../utils/helpers";
import { TOKEN_RAIN, TOKEN_USDT } from "../../utils/constants";

const ALL_PRESETS = [50, 100, 250, 500, 1000];

export const LiquidityWithFees = ({
  onConfirm,
  initialCurrency = "usdr",
  initialAmount,
  maxBalance,
  gasFeeInUsdt,
  oracleFeeInUsdt,
  isLoadingFees,
  onAmountChange,
}) => {
  const maxAmount = maxBalance ? Math.floor(maxBalance * 100) / 100 : 2000;
  const clampedMax = Math.max(0.01, maxAmount);

  const startingAmount =
    initialAmount != null && initialAmount > 0
      ? Math.min(initialAmount, clampedMax)
      : Math.min(10, clampedMax);
  const [amount, setAmount] = useState(startingAmount);
  const percentage = ((amount - 0.01) / (clampedMax - 0.01)) * 100;
  const PRESETS = ALL_PRESETS.filter((p) => p <= clampedMax);
  const [currency, setCurrency] = useState(initialCurrency);
  const [showFees, setShowFees] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(startingAmount.toString());
  const inputDebounceRef = useRef(null);

  const measureRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(40);

  useEffect(() => {
    if (measureRef.current) {
      const width = measureRef.current.scrollWidth;
      setInputWidth(Math.max(40, width * 3));
    }
  }, [inputValue]);

  useEffect(() => {
    if (onAmountChange) {
      onAmountChange(amount, currency);
    }
  }, [amount, currency]);

  useEffect(() => {
    return () => {
      if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current);
    };
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current);
    inputDebounceRef.current = setTimeout(() => {
      const parsed = parseFloat(val);
      if (!isNaN(parsed) && parsed >= 0.01 && parsed <= clampedMax) {
        setAmount(parsed);
      }
    }, 600);
  };

  const handleInputBlur = () => {
    if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current);
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed) && parsed >= 0.01 && parsed <= clampedMax) {
      setAmount(parsed);
      setInputValue(parsed.toString());
    } else {
      setInputValue(amount.toString());
    }
    setIsEditing(false);
  };
  const networkFee = gasFeeInUsdt != null ? gasFeeInUsdt : null;
  const oracleFee = oracleFeeInUsdt != null ? oracleFeeInUsdt : null;

  const feesReady = networkFee != null && oracleFee != null && !isLoadingFees;
  const totalFees = feesReady ? networkFee + oracleFee : null;
  const netLiquidity = feesReady ? Math.max(0, amount - oracleFee) : null;

  const currencySymbol = currency === "usdr" ? "$" : "";
  const currencyLabel = currency === "usdr" ? "USD" : "RAIN";

  const CurrencyIcon = () => {
    if (currency === "rain") {
      return (
        <img
          src="\assets\genrate-market-imgs\rain.svg"
          alt="img"
          className="img-fluid tokensize"
        />
      );
    }
    return (
      <img
        src="/assets/poll/usdr.svg"
        alt="img"
        className="img-fluid tokensize"
      />
    );
  };

  const handleConfirm = () => {
    if (!feesReady) return;
    onConfirm(amount, currency);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="gm-liq"
    >
      <div className="gm-liq-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
        >
          <path
            d="M12 5.25H16.5V9.75"
            stroke="#E6FF00"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M16.5 5.25L10.125 11.625L6.375 7.875L1.5 12.75"
            stroke="#E6FF00"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>Add initial liquidity</span>
      </div>

      <div className="parent-box">
        <div className="gm-liq-currency-center">
          <CurrencySelector
            selected={currency}
            onChange={setCurrency}
            compact
          />
        </div>

        <div className="gm-liq-amount-display">
          <motion.div
            key={`${amount}-${currency}`}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="gm-liq-amount-inner"
            style={{ position: "relative" }}
          >
            <CurrencyIcon />
            {isEditing ? (
              <>
                <input
                  type="number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleInputBlur();
                    if (e.key === "Escape") {
                      setIsEditing(false);
                      setInputValue(amount.toString());
                    }
                  }}
                  autoFocus
                  className="gm-liq-amount-input"
                  style={{ width: `${inputWidth}px` }}
                  min={0.01}
                  max={clampedMax}
                />
                <span
                  ref={measureRef}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    visibility: "hidden",
                    height: 0,
                    overflow: "hidden",
                    whiteSpace: "pre",
                    fontSize: "inherit",
                    fontFamily: "inherit",
                    fontWeight: "inherit",
                    letterSpacing: "inherit",
                  }}
                >
                  {inputValue || "0"}
                </span>
              </>
            ) : (
              <motion.button
                onClick={() => setIsEditing(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="gm-liq-amount-btn"
              >
                {amount}
              </motion.button>
            )}
            <span className="gm-liq-currency-label">{currencyLabel}</span>
          </motion.div>
        </div>

        <div className="gm-liq-slider-wrap">
          <input
            type="range"
            value={amount}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setAmount(v);
              setInputValue(v.toString());
            }}
            min={0.01}
            max={clampedMax}
            step={0.01}
            className="gm-liq-slider"
            style={{ "--progress": `${percentage}%` }}
          />
          <div className="gm-liq-slider-range">
            <span>{currencySymbol}0.01</span>
            <span>
              {currencySymbol}
              {clampedMax.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="gm-liq-presets">
          {PRESETS.map((preset) => (
            <motion.button
              key={preset}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setAmount(preset);
                setInputValue(preset.toString());
              }}
              className={`gm-liq-preset-btn ${amount === preset ? "gm-liq-preset-btn--active" : ""}`}
            >
              {currencySymbol}
              {preset}
            </motion.button>
          ))}
        </div>

        <div className="gm-liq-fees-card">
          <button
            onClick={() => setShowFees(!showFees)}
            className="gm-liq-fees-toggle"
            style={{
              borderBottomRightRadius: showFees ? "0" : "14px",
              borderBottomLeftRadius: showFees ? "0" : "14px",
            }}
          >
            <span className="gm-liq-fees-label">Net Liquidity</span>
            <div className="gm-liq-fees-value-wrap">
              <span className="gm-liq-fees-value">
                {isLoadingFees ? (
                  <Loader className="gm-icon-xs gm-spin" />
                ) : feesReady ? (
                  <>
                    {getTokenSymbol(
                      currency === "rain" ? TOKEN_RAIN : TOKEN_USDT,
                    )}
                    {formatSmartDecimal(+netLiquidity.toFixed(4))}{" "}
                    {currencyLabel}
                  </>
                ) : (
                  "— " + currencyLabel
                )}
              </span>
              <motion.div
                animate={{ rotate: showFees ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M3.1333 6.71141L7.67843 10.8778C7.72064 10.9165 7.77077 10.9473 7.82595 10.9682C7.88113 10.9892 7.94027 11 8 11C8.05973 11 8.11887 10.9892 8.17405 10.9682C8.22923 10.9473 8.27936 10.9165 8.32157 10.8778L12.8667 6.71141C12.9303 6.65314 12.9737 6.57888 12.9912 6.49801C13.0088 6.41715 12.9998 6.33333 12.9654 6.25715C12.931 6.18098 12.8726 6.11589 12.7978 6.07011C12.723 6.02434 12.6351 5.99994 12.5451 6L3.45487 6C3.36493 5.99994 3.27698 6.02434 3.20217 6.07011C3.12736 6.11589 3.06905 6.18098 3.03462 6.25715C3.00019 6.33333 2.99119 6.41715 3.00875 6.49801C3.02632 6.57888 3.06967 6.65314 3.1333 6.71141Z"
                    fill="#DAD9D9"
                  />
                </svg>
              </motion.div>
            </div>
          </button>

          <AnimatePresence>
            {showFees && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="gm-liq-fees-detail"
              >
                <div className="gm-liq-fee-row">
                  <div className="gm-liq-fee-label">
                    <span>Estimated gas fee</span>
                    <div className="tooltipicon firsttooltip">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="icon"
                      >
                        <g clip-path="url(#clip0_1716_1320)">
                          <path
                            d="M6.99996 12.8332C10.2216 12.8332 12.8333 10.2215 12.8333 6.99984C12.8333 3.77818 10.2216 1.1665 6.99996 1.1665C3.7783 1.1665 1.16663 3.77818 1.16663 6.99984C1.16663 10.2215 3.7783 12.8332 6.99996 12.8332Z"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7 9.33333V7"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7 4.6665H7.00625"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1716_1320">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <div className="tooltipmain">
                        Blockchain transaction fee. Charged separately from your
                        wallet.
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="10"
                          viewBox="0 0 13 10"
                          fill="none"
                          className="tooltiparrow"
                        >
                          <path
                            d="M7.30183 8.93333C6.90183 9.46667 6.10183 9.46667 5.70183 8.93333L0.201832 1.6C-0.292596 0.940763 0.177786 -2.54862e-07 1.00183 -1.82822e-07L12.0018 7.78829e-07C12.8259 8.50869e-07 13.2963 0.940764 12.8018 1.6L7.30183 8.93333Z"
                            fill="#2A2A2A"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <span className="gm-liq-fee-amount">
                    {isLoadingFees ? (
                      <Loader className="gm-icon-xs gm-spin" />
                    ) : networkFee != null ? (
                      `$${networkFee.toFixed(4)}`
                    ) : (
                      "—"
                    )}
                  </span>
                </div>

                <div className="gm-liq-fee-row">
                  <div className="gm-liq-fee-label">
                    <span>Oracle Fixed Fee</span>
                    <div className="tooltipicon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        className="icon"
                      >
                        <g clip-path="url(#clip0_1716_1320)">
                          <path
                            d="M6.99996 12.8332C10.2216 12.8332 12.8333 10.2215 12.8333 6.99984C12.8333 3.77818 10.2216 1.1665 6.99996 1.1665C3.7783 1.1665 1.16663 3.77818 1.16663 6.99984C1.16663 10.2215 3.7783 12.8332 6.99996 12.8332Z"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7 9.33333V7"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M7 4.6665H7.00625"
                            stroke="#797979"
                            stroke-width="1.16667"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1716_1320">
                            <rect width="14" height="14" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      <div className="tooltipmain">
                        Fixed fee for market resolution. Deducted from your
                        entered amount.
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="13"
                          height="10"
                          viewBox="0 0 13 10"
                          fill="none"
                          className="tooltiparrow"
                        >
                          <path
                            d="M7.30183 8.93333C6.90183 9.46667 6.10183 9.46667 5.70183 8.93333L0.201832 1.6C-0.292596 0.940763 0.177786 -2.54862e-07 1.00183 -1.82822e-07L12.0018 7.78829e-07C12.8259 8.50869e-07 13.2963 0.940764 12.8018 1.6L7.30183 8.93333Z"
                            fill="#2A2A2A"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <span className="gm-liq-fee-amount">
                    {isLoadingFees ? (
                      <Loader className="gm-icon-xs gm-spin" />
                    ) : oracleFee != null ? (
                      <>
                        {getTokenSymbol(
                          currency === "rain" ? TOKEN_RAIN : TOKEN_USDT,
                        )}
                        {formatSmartDecimal(oracleFee)} {currencyLabel}
                      </>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>

                <div className="gm-liq-fee-row gm-liq-fee-row--total">
                  <span className="gm-liq-fee-total-label">
                    Total Deduction
                  </span>
                  <span className="gm-liq-fee-total-value">
                    {isLoadingFees ? (
                      <Loader className="gm-icon-xs gm-spin" />
                    ) : feesReady && currency === "rain" ? (
                      <>
                        {getTokenSymbol(TOKEN_RAIN)}
                        {formatNumbers(+amount)} + ${formatNumbers(+networkFee)}
                      </>
                    ) : feesReady ? (
                      <>
                        {getTokenSymbol(TOKEN_USDT)}
                        {formatNumbers(+(amount + networkFee))}
                      </>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <button
          onClick={handleConfirm}
          disabled={!feesReady}
          className="gm-primary-btn"
          style={{
            opacity: feesReady ? 1 : 0.5,
            cursor: feesReady ? "pointer" : "not-allowed",
          }}
        >
          {isLoadingFees ? (
            <>
              <Loader className="gm-icon-sm gm-spin" />
              Calculating fees...
            </>
          ) : (
            <>
              Add {" "}
              {currencyLabel === "USD" && ( currencySymbol )}
              {amount}{" "}
              {currencyLabel === "RAIN" && ( currencyLabel )} Liquidity
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};
